import React, {Component} from 'react';
import moment from 'moment';
import {Button, Loader} from 'semantic-ui-react';
import {yearFormat, monthFormat} from '../../constants/datesFormat';
import {getMonthShortName, getLastDayOfMonth} from '../../../common/util/date';
import './dateFiltersSection.scss';

const currentYear = parseInt(moment().format(yearFormat), 10);
const currentMonth = parseInt(moment().format(monthFormat), 10);

class FiltersSection extends Component {
  constructor(props) {
    super(props);
    this.state = {
      'yearSelected': currentYear,
      'monthSelected': currentMonth,
      'initialDaySelected': '',
      'finalDaySelected': '',
    };
  }

  componentDidMount() {
    this.getDateFilters();
  }

  componentDidUpdate(prevProps, prevState) {
    const {clearFilters} = this.props;
    const {yearSelected, monthSelected} = this.state;

    if (clearFilters.year && yearSelected) {
      this.onUpdateYearClick('');
      return;
    }
    if (clearFilters.month && monthSelected) {
      this.onUpdateMonthClick('');
      return;
    }
    if (!clearFilters.day) {
      return;
    }
    this.onUpdateDayClick('CLEAR');
  }

  getDateFilters = () => {
    const {yearSelected, monthSelected, initialDaySelected, finalDaySelected} = this.state;
    const rangeDate = this.getRangeDate();
    const queryString = {
      'startDate': rangeDate.startDate,
      'endDate': rangeDate.endDate,
      'year': yearSelected,
      'month': monthSelected,
      'initialDay': initialDaySelected,
      'finalDay': finalDaySelected,
    };
    this.props.onDateFiltersClick(queryString);
  }

  formatNumber = (number) => {
    if (number < 10) {
      return `0${number}`;
    }
    return number;
  }

  getRangeDate = () => {
    const {yearSelected, monthSelected, initialDaySelected, finalDaySelected} = this.state;
    const month = this.formatNumber(monthSelected);
    const initialDay = this.formatNumber(initialDaySelected);
    const finalDay = this.formatNumber(finalDaySelected);
    
    if (!yearSelected && !monthSelected && !initialDaySelected && !finalDaySelected) {
      return {
        'startDate': null,
        'endDate': null,
      };
    }
    if (!monthSelected && !initialDaySelected && !finalDaySelected) {
      return {
        'startDate': `${yearSelected}-01-01`,
        'endDate': `${yearSelected}-12-31`,
      };
    }
    if (!initialDaySelected && !finalDaySelected) {
      const lastDay = getLastDayOfMonth(yearSelected, monthSelected);
      return {
        'startDate': `${yearSelected}-${month}-01`,
        'endDate': `${yearSelected}-${month}-${lastDay}`,
      };
    }
    if (!finalDaySelected) {
      return {
        'startDate': `${yearSelected}-${month}-${initialDay}`,
        'endDate': `${yearSelected}-${month}-${initialDay}`,
      };
    }
    return {
      'startDate': `${yearSelected}-${month}-${initialDay}`,
      'endDate': `${yearSelected}-${month}-${finalDay}`,
    };
  }

  getYears = () => {
    const {years, loadingYears} = this.props;
    const {yearSelected} = this.state;

    if (loadingYears) {
      return <Loader active inline='centered' />;
    }
    return years.map((element, index) => {
      const isPrimaryButton = element.year === yearSelected;
      return (
        <Button
          key={index}
          type='button'
          primary={isPrimaryButton}
          secondary={!isPrimaryButton}
          disabled={loadingYears}
          loading={loadingYears}
          onClick={() => this.onUpdateYearClick(element.year)}
        >
          {element.year}
        </Button>
      );
    });
  }

  onUpdateYearClick = (year) => {
    const newState = {'yearSelected': year};
    if (!year) {
      newState.monthSelected = '';
      newState.initialDaySelected = '';
      newState.finalDaySelected = '';
      this.setState(newState, () => this.getDateFilters());
      return;
    }

    const {monthSelected, initialDaySelected, finalDaySelected} = this.state;
    const lastDayOfMonth = year && monthSelected && (initialDaySelected || finalDaySelected) ?
      getLastDayOfMonth(year, monthSelected) : '';
    if (monthSelected && finalDaySelected && finalDaySelected > lastDayOfMonth) {
      newState.finalDaySelected = lastDayOfMonth;
    }
    if (monthSelected && initialDaySelected && initialDaySelected > lastDayOfMonth) {
      newState.initialDaySelected = lastDayOfMonth;
    }
    this.setState(newState, () => this.getDateFilters());
  }

  getMonths = () => {
    const {yearSelected, monthSelected} = this.state;
    const months = [];
    for (let i = 1; i <= 12; i ++) {
      months.push(i);
    }

    return months.map((month, index) => {
      const isPrimaryButton = month === monthSelected;
      return (
        <Button
          key={index}
          type='button'
          primary={isPrimaryButton}
          secondary={!isPrimaryButton}
          disabled={!yearSelected}
          onClick={() => this.onUpdateMonthClick(month)}
        >
          {getMonthShortName(month)}
        </Button>
      );
    });
  }

  onUpdateMonthClick = (month) => {
    const newState = {'monthSelected': month};
    if (!month) {
      newState.initialDaySelected = '';
      newState.finalDaySelected = '';
      this.setState(newState, () => this.getDateFilters());
      return;
    }

    const {yearSelected, initialDaySelected, finalDaySelected} = this.state;
    const lastDayOfMonth = yearSelected && month && (initialDaySelected || finalDaySelected) ?
      getLastDayOfMonth(yearSelected, month) : '';
    if (finalDaySelected && finalDaySelected > lastDayOfMonth) {
      newState.finalDaySelected = lastDayOfMonth;
    }
    if (initialDaySelected && initialDaySelected > lastDayOfMonth) {
      newState.initialDaySelected = lastDayOfMonth;
    }
    this.setState(newState, () => this.getDateFilters());
  }

  getDays = () => {
    const {showDays} = this.props;
    if (!showDays) {
      return null;
    }
    
    const {yearSelected, monthSelected, initialDaySelected, finalDaySelected} = this.state;
    const lastDayOfMonth = yearSelected && monthSelected ? getLastDayOfMonth(yearSelected, monthSelected) : 0;
    const days = [];
    for (let i = 1; i <= 31; i ++) {
      days.push(i);
    }
    return days.map((day, index) => {
      const isPrimaryButton = day === initialDaySelected || day === finalDaySelected ||
        (day > initialDaySelected && day < finalDaySelected);
      return (
        <Button
          key={index}
          type='button'
          primary={isPrimaryButton}
          secondary={!isPrimaryButton}
          className='toyota-primary'
          disabled={!yearSelected || !monthSelected || day > lastDayOfMonth}
          onClick={() => this.onUpdateDayClick(day)}
        >
          {day}
        </Button>
      );
    });
  }

  onUpdateDayClick = (day) => {
    const {initialDaySelected, finalDaySelected} = this.state;
    if (day === 'CLEAR') {
      this.setState({
        'initialDaySelected': '',
        'finalDaySelected': '',
      }, () => this.getDateFilters());
      return;
    }
    if ((!initialDaySelected && !finalDaySelected) ||
      (initialDaySelected && finalDaySelected && day < initialDaySelected)) {
      this.setState({'initialDaySelected': day}, () => this.getDateFilters());
      return;
    }
    if (!finalDaySelected && day < initialDaySelected) {
      this.setState({
        'initialDaySelected': day,
        'finalDaySelected': initialDaySelected,
      }, () => this.getDateFilters());
      return;
    }
    this.setState({'finalDaySelected': day}, () => this.getDateFilters());
  }

  render() {
    return (
      <div className='filters-container'>
        <div>
          <span className='years-container'>
            {this.getYears()}
          </span>
          {this.getMonths()}
        </div>
        <div className='days-container'>
          {this.getDays()}
        </div>
      </div>
    );
  }
}

export default FiltersSection;
